import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface Rect { left: number; top: number; width: number; height: number }

const toRect = (el: Element): Rect | null => {
  const r = el.getBoundingClientRect();
  // A zero box means hidden (rail label, collapsed panel) — no ring, rather
  // than a ring around nothing.
  if (r.width < 1 || r.height < 1) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

const same = (a: Rect | null, b: Rect | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5
  );
};

const selectorFor = (targetKey: string) => `[data-help="${CSS.escape(targetKey)}"]`;

/** The step's anchors, in the order the content listed them. */
const asList = (target?: string | string[]): string[] =>
  (!target ? [] : Array.isArray(target) ? target : [target]);

export const findAnchor = (target?: string | string[]): Element | null => {
  for (const key of asList(target)) {
    const found = document.querySelector(selectorFor(key));
    if (found) return found;
  }
  return null;
};

/**
 * Is something else painted over this element?
 *
 * The ring sits above the app's modals on purpose, so a step can point at the
 * Publish button or the photo-check inside the Review & Publish window. The
 * cost of that is a ring for a target BEHIND a modal bleeding over the front of
 * it — the posts list showing through the review window, the schedule showing
 * through the document viewer.
 *
 * Hit-testing rather than a list of overlay selectors and z-indexes (1055 for
 * the Bootstrap-markup modals, 1080 for Review & Publish, 2100 for the PDF
 * viewer): the next overlay someone adds would not be on that list, and this
 * asks the browser what is actually on top. Help's own window never counts —
 * it moves aside for the ring, and while it is moving it must not blank it.
 */
const isCovered = (el: Element): boolean => {
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  // Off-screen is not covered; the caller clips those to the viewport instead.
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;
  // No hit testing available (jsdom, and any environment without layout): assume
  // visible. Losing the ring would be the worse failure of the two.
  if (typeof document.elementFromPoint !== 'function') return false;
  const hit = document.elementFromPoint(x, y);
  if (!hit) return false;
  if (hit === el || el.contains(hit) || hit.contains(el)) return false;
  if (hit.closest('.help-window')) return false;
  return true;
};

/**
 * The union of every element carrying the anchor. A selected session in the
 * schedule grid is drawn as a run of adjacent cells, all of them the same
 * session — ringing only the first would point at a fragment of the bar the
 * step is talking about.
 */
const measureAnchor = (target?: string | string[]): Rect | null => {
  // First anchor with something visible behind it wins. The order is the
  // content's: most specific first, so arriving on a page moves the ring from
  // the menu item that got you there to the thing you now need.
  for (const key of asList(target)) {
    const rect = measureOne(key);
    if (rect) return rect;
  }
  return null;
};

const measureOne = (targetKey: string): Rect | null => {
  const parts: Rect[] = [];
  const found = Array.from(document.querySelectorAll(selectorFor(targetKey)));
  // Hit-testing is capped: a grid of tiles can carry the same anchor dozens of
  // times, and this runs every frame. A dozen is far more than enough to tell a
  // covered target from a visible one.
  const checkLimit = 12;
  found.forEach((el, i) => {
    const r = toRect(el);
    if (!r) return;
    if (i < checkLimit && isCovered(el)) return;
    parts.push(r);
  });
  // Every part of it is behind something: there is nothing to point at, and a
  // ring here would be drawn over whatever is covering it.
  if (parts.length === 0) return null;
  // Clipped to the viewport: a scrolling list (the child picker's tiles) has
  // members below the fold, and a ring drawn around all of them would run off
  // the screen instead of framing what the user can see. The inset leaves room
  // for the ring's own padding.
  const inset = 10;
  const left = Math.max(Math.min(...parts.map(p => p.left)), inset);
  const top = Math.max(Math.min(...parts.map(p => p.top)), inset);
  const right = Math.min(Math.max(...parts.map(p => p.left + p.width)), window.innerWidth - inset);
  const bottom = Math.min(Math.max(...parts.map(p => p.top + p.height)), window.innerHeight - inset);
  if (right <= left || bottom <= top) return null;
  return { left, top, width: right - left, height: bottom - top };
};

/**
 * Tracks what the window has to avoid: the highlighted anchor, and any modal
 * the app has opened on top (the schedule's scope modal is the real case).
 *
 * Measured every frame rather than by observer: the anchor can be resized by a
 * drag in progress (the session bar the user is stretching), and a single
 * getBoundingClientRect per frame is cheaper than keeping observers in sync
 * with elements that mount and unmount under it. State only updates when a
 * rect actually moves.
 */
export const useHelpRects = (
  target?: string | string[],
): { target: Rect | null; modal: Rect | null } => {
  const [rects, setRects] = useState<{ target: Rect | null; modal: Rect | null }>(
    { target: null, modal: null },
  );

  useEffect(() => {
    let frame = 0;
    let stopped = false;

    // Nothing to track while browsing: no ring, and a window that isn't
    // pointing at anything has nothing to dodge. Measuring anyway would burn a
    // DOM query every frame for the whole time help is open.
    if (!target || (Array.isArray(target) && target.length === 0)) {
      setRects(prev => (prev.target === null && prev.modal === null
        ? prev
        : { target: null, modal: null }));
      return undefined;
    }

    const measure = () => {
      const targetRect = measureAnchor(target);
      const dialog = document.querySelector('.modal.show .modal-dialog');
      const modal = dialog ? toRect(dialog) : null;
      setRects(prev => (same(prev.target, targetRect) && same(prev.modal, modal)
        ? prev
        : { target: targetRect, modal }));
      if (!stopped) frame = requestAnimationFrame(measure);
    };

    frame = requestAnimationFrame(measure);
    return () => { stopped = true; cancelAnimationFrame(frame); };
    // A list is rebuilt on every render, so depend on its contents rather than
    // its identity — otherwise this restarts the loop sixty times a second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(target) ? target.join('|') : target]);

  return rects;
};

/**
 * The ring around the step's target. `pointer-events: none` throughout — it
 * must never intercept a click or, more to the point, the drag in step 5.
 * No scrim: the user is working on this page.
 *
 * `arriveKey` replays the entrance animation. It changes when the ring moves to
 * a DIFFERENT element — a new step, or an anchor that has only now rendered —
 * and not when the page reflows under it, so the pulse marks "look here" rather
 * than twitching every time a panel opens. Same discipline as the window's
 * placement, for the same reason.
 *
 * `locate` counts "Show me" presses for this step. Each one remounts the ring
 * and sends a second ring in from the edges of the screen to land on it: by the
 * time someone asks, the arrival pulse has already been missed once, and
 * replaying the same small pulse would be missed the same way. A ring that
 * starts where the user is already looking and travels to the target can be
 * followed. Only ever on request — see the note on the ring in help.scss.
 */
const HelpSpotlight: React.FC<{ rect: Rect | null; arriveKey?: string; locate?: number }> = ({
  rect, arriveKey, locate = 0,
}) => {
  if (!rect) return null;
  const pad = 6;
  const box = {
    left: rect.left - pad,
    top: rect.top - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
  const key = `${arriveKey ?? ''}#${locate}`;
  return createPortal(
    <>
      <div
        key={key}
        className={`help-spotlight${locate > 0 ? ' is-locating' : ''}`}
        aria-hidden="true"
        style={box}
      />
      {locate > 0 && (
        <div
          key={`${key}:locate`}
          className="help-spotlight-locate"
          aria-hidden="true"
          // The keyframes start from the viewport and end on these values, so
          // the landing follows the target if it is still moving (a smooth
          // scroll bringing it into view).
          style={box}
        />
      )}
    </>,
    document.body,
  );
};

export default HelpSpotlight;
